import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Eye, EyeOff, Search, Send, Trash2, Undo2 } from 'lucide-react';
import { campiAPI, schedeAPI } from '../services/api';
import '../styles/Catalogo.css';

/**
 * MieSchede — la scheda che il medico compone per sé.
 *
 * COMPONE, NON CONIA
 * Si parte da una scheda della struttura e si registrano le differenze:
 * campi aggiunti dal catalogo, campi nascosti. Non si copia la scheda: se
 * la struttura aggiorna la propria, questa segue. Una copia resterebbe
 * ferma al giorno in cui è stata fatta, e sei mesi dopo nessuno saprebbe
 * più quali differenze erano volute e quali sono solo il tempo passato.
 *
 * SE UN CAMPO NON C'E', SI CHIEDE
 * Non si crea. La richiesta arriva all'amministrazione con il motivo, e
 * finché non decide quel campo non esiste per nessuno — nemmeno per chi
 * l'ha chiesto. Senza questa regola, in un anno il catalogo ha tre «peso»
 * diversi e gli andamenti nel tempo non si possono più calcolare.
 */

const MieSchede = () => {
    const [mie, setMie] = useState([]);
    const [disponibili, setDisponibili] = useState([]);
    const [aperta, setAperta] = useState(null);
    const [catalogo, setCatalogo] = useState([]);
    const [aree, setAree] = useState([]);
    const [cerca, setCerca] = useState('');
    const [areaFiltro, setAreaFiltro] = useState('');
    const [inSezione, setInSezione] = useState(null);
    const [richiesta, setRichiesta] = useState(null);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [negato, setNegato] = useState(false);

    const carica = useCallback(async () => {
        try {
            const r = await schedeAPI.mie();
            setMie(r.data.data.mie);
            setDisponibili(r.data.data.disponibili);
        } catch (e) {
            if (e?.response?.status === 403) setNegato(true);
            else setErrore('Non riesco a leggere le tue schede');
        }
    }, []);

    useEffect(() => {
        carica();
        campiAPI.aree().then((r) => setAree(r.data.data.aree)).catch(() => {});
    }, [carica]);

    const apri = async (id) => {
        setErrore(null);
        try { const r = await schedeAPI.variante(id); setAperta(r.data.data); }
        catch (e) { setErrore(e?.response?.data?.message || 'Non riesco ad aprire la scheda'); }
    };

    useEffect(() => {
        if (!inSezione) return;
        const t = setTimeout(async () => {
            try {
                const r = await campiAPI.elenco({
                    cerca: cerca || undefined, area: areaFiltro || undefined, stato: 'attivo',
                });
                setCatalogo(r.data.data.campi);
            } catch (e) { /* la ricerca che non torna non blocca la schermata */ }
        }, 250);
        return () => clearTimeout(t);
    }, [cerca, areaFiltro, inSezione]);

    const parti = async (schedaId) => {
        try { const r = await schedeAPI.creaVariante(schedaId, {}); carica(); apri(r.data.data.variante.id); }
        catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a creare la tua versione'); }
    };

    const differenza = async (dati) => {
        setErrore(null);
        try { await schedeAPI.differenza(aperta.variante.id, dati); apri(aperta.variante.id); }
        catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a salvare'); }
    };

    const mostraDiNuovo = async (campoId) => {
        const d = aperta.differenze.find((x) => x.azione === 'nasconde' && x.campo_id === campoId);
        if (!d) return;
        try { await schedeAPI.togliDifferenza(aperta.variante.id, d.id); apri(aperta.variante.id); }
        catch (e) { setErrore('Non riesco a rimettere il campo'); }
    };

    const togliAggiunto = async (diffId) => {
        try { await schedeAPI.togliDifferenza(aperta.variante.id, diffId); apri(aperta.variante.id); }
        catch (e) { setErrore('Non riesco a togliere il campo'); }
    };

    const inviaRichiesta = async (e) => {
        e.preventDefault();
        setErrore(null);
        try {
            await campiAPI.proponi({ ...richiesta, scheda_id: aperta?.variante?.base_id });
            setRichiesta(null);
            setAvviso('Richiesta inviata all’amministrazione. Il campo comparirà quando verrà approvato.');
        } catch (err) { setErrore(err?.response?.data?.message || 'Non riesco a inviare la richiesta'); }
    };

    if (negato) {
        return (
            <div className="cat-pagina">
                <h1>Le mie schede</h1>
                <p className="cat-vuoto">
                    Le schede personali sono dei medici: il tuo utente non è collegato a un medico.
                </p>
            </div>
        );
    }

    const dentro = new Set((aperta?.sezioni || []).flatMap((z) => z.campi.map((c) => c.campo_id)));

    return (
        <div className="cat-pagina">
            <header className="cat-testa">
                <div>
                    <h1>Le mie schede</h1>
                    <p>Parti da una scheda della struttura e la adatti: aggiungi campi dal catalogo,
                       nascondi quelli che non usi. Se la struttura aggiorna la sua, la tua segue.</p>
                </div>
            </header>

            {avviso && <p className="cat-avviso">{avviso} <button onClick={() => setAvviso(null)}>×</button></p>}
            {errore && <p className="cat-errore">{errore}</p>}

            <div className="cat-due">
                <aside className="cat-elenco">
                    {mie.map((s) => (
                        <button key={s.id} className={`cat-voce ${aperta?.variante?.id === s.id ? 'aperta' : ''}`}
                                onClick={() => apri(s.id)}>
                            <div>
                                <strong>{s.nome || s.base_nome}</strong>
                                <span className="cat-meta">
                                    {s.area_nome || 'senza area'}
                                    {Number(s.differenze) > 0
                                        ? ` · ${s.differenze} differenze dalla scheda della struttura`
                                        : ' · uguale a quella della struttura'}
                                </span>
                            </div>
                        </button>
                    ))}
                    {mie.length === 0 && <p className="cat-vuoto">Non hai ancora una scheda tua.</p>}

                    {disponibili.length > 0 && (
                        <div className="cat-disponibili">
                            <h3>Da cui puoi partire</h3>
                            {disponibili.map((s) => (
                                <button key={s.id} className="cat-voce" onClick={() => parti(s.id)}>
                                    <div>
                                        <strong>{s.nome}</strong>
                                        <span className="cat-meta">{s.area_nome || 'senza area'}</span>
                                    </div>
                                    <Plus size={16} aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                    )}
                </aside>

                <section className="cat-dettaglio">
                    {!aperta ? <p className="cat-vuoto">Scegli una scheda a sinistra.</p> : (
                        <>
                            <div className="cat-testa-scheda">
                                <div>
                                    <h2>{aperta.variante.nome || aperta.variante.base_nome}</h2>
                                    <span className="cat-meta">
                                        parte da «{aperta.variante.base_nome}» della struttura
                                    </span>
                                </div>
                                <button className="btn-no" onClick={async () => {
                                    if (!window.confirm('Eliminare la tua versione?\n\nLa scheda della '
                                        + 'struttura resta: torni a usare quella.')) return;
                                    await schedeAPI.eliminaVariante(aperta.variante.id);
                                    setAperta(null); carica();
                                }}>
                                    <Trash2 size={15} aria-hidden="true" /> Elimina la mia versione
                                </button>
                            </div>

                            {aperta.sezioni.map((z) => (
                                <div key={z.id} className="cat-sezione">
                                    <h3>{z.titolo}<span className="cat-meta">
                                        {z.campi.filter((c) => !c.nascosto).length} campi
                                    </span></h3>
                                    <ul className="cat-campi">
                                        {z.campi.map((c) => (
                                            <li key={c.id || `agg-${c.differenza_id}`}
                                                className={c.nascosto ? 'cat-nascosto' : ''}>
                                                <span className="cat-nome">{c.nome}</span>
                                                <span className="cat-meta">
                                                    {c.unita || ''}{c.aggiunto ? ' · aggiunto da te' : ''}
                                                </span>
                                                {c.aggiunto ? (
                                                    <button className="btn-icona" title="Togli"
                                                            onClick={() => togliAggiunto(c.differenza_id)}>
                                                        <Trash2 size={14} aria-hidden="true" />
                                                    </button>
                                                ) : c.nascosto ? (
                                                    <button className="btn-icona" title="Rimetti"
                                                            onClick={() => mostraDiNuovo(c.campo_id)}>
                                                        <Eye size={14} aria-hidden="true" />
                                                    </button>
                                                ) : (
                                                    <button className="btn-icona" title="Nascondi"
                                                            onClick={() => differenza({ azione: 'nasconde', campo_id: c.campo_id })}>
                                                        <EyeOff size={14} aria-hidden="true" />
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    <button className="btn-testo" onClick={() => {
                                        setInSezione(z.id); setCerca(''); setAreaFiltro('');
                                    }}>
                                        <Plus size={14} aria-hidden="true" /> Aggiungi un campo dal catalogo
                                    </button>
                                </div>
                            ))}

                            <p className="cat-nota">
                                <Undo2 size={14} aria-hidden="true" /> Un campo nascosto non viene cancellato:
                                sparisce dalla tua scheda e resta in quella della struttura. Puoi rimetterlo
                                quando vuoi.
                            </p>
                        </>
                    )}
                </section>
            </div>

            {inSezione && (
                <div className="cat-velo" onClick={(e) => { if (e.target === e.currentTarget) setInSezione(null); }}>
                    <div className="cat-riquadro">
                        <h2>Scegli dal catalogo</h2>
                        <div className="cat-filtri">
                            <label className="cat-cerca">
                                <Search size={15} aria-hidden="true" />
                                <input autoFocus value={cerca} onChange={(e) => setCerca(e.target.value)}
                                       placeholder="Cerca per nome" />
                            </label>
                            <select value={areaFiltro} onChange={(e) => setAreaFiltro(e.target.value)}>
                                <option value="">Tutte le aree</option>
                                {aree.map((a) => <option key={a.id} value={a.codice}>
                                    {a.nome}{a.trasversale ? ' (trasversale)' : ''}</option>)}
                            </select>
                        </div>
                        <ul className="cat-scelta">
                            {catalogo.map((c) => (
                                <li key={c.id}>
                                    <button disabled={dentro.has(c.id)}
                                            onClick={() => { differenza({ azione: 'aggiunge', campo_id: c.id, sezione_id: inSezione }); setInSezione(null); }}>
                                        <span className="cat-nome">{c.nome}</span>
                                        <span className="cat-meta">
                                            {c.area_nome} · {c.tipo.replace('_', ' ')}{c.unita ? ` · ${c.unita}` : ''}
                                        </span>
                                        {dentro.has(c.id) && <span className="cat-tag">già nella scheda</span>}
                                    </button>
                                </li>
                            ))}
                            {catalogo.length === 0 && <li className="cat-vuoto">Nessun campo trovato.</li>}
                        </ul>
                        <div className="cat-pulsanti">
                            <button className="btn-secondario" onClick={() => {
                                setInSezione(null);
                                setRichiesta({ nome: cerca, area_id: '', tipo: 'testo', unita: '', motivo: '' });
                            }}>
                                <Send size={15} aria-hidden="true" /> Non c’è: chiedilo
                            </button>
                            <button className="btn-annulla" onClick={() => setInSezione(null)}>Chiudi</button>
                        </div>
                    </div>
                </div>
            )}

            {richiesta && (
                <div className="cat-velo" onClick={(e) => { if (e.target === e.currentTarget) setRichiesta(null); }}>
                    <form className="cat-riquadro" onSubmit={inviaRichiesta}>
                        <h2>Chiedi un campo nuovo</h2>
                        <p className="cat-nota">
                            La richiesta va all’amministrazione. Finché non viene approvata il campo non
                            esiste per nessuno, nemmeno per te: è quello che evita che in un anno il
                            catalogo abbia tre «peso» diversi e gli andamenti nel tempo non si possano
                            più calcolare.
                        </p>
                        {errore && <p className="cat-errore">{errore}</p>}
                        <div className="cat-riga">
                            <label>
                                Come si chiama
                                <input required value={richiesta.nome}
                                       onChange={(e) => setRichiesta({ ...richiesta, nome: e.target.value })} />
                            </label>
                            <label>
                                Area
                                <select required value={richiesta.area_id}
                                        onChange={(e) => setRichiesta({ ...richiesta, area_id: e.target.value })}>
                                    <option value="">—</option>
                                    {aree.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
                                </select>
                            </label>
                        </div>
                        <div className="cat-riga">
                            <label>
                                Che tipo di dato
                                <select value={richiesta.tipo}
                                        onChange={(e) => setRichiesta({ ...richiesta, tipo: e.target.value })}>
                                    <option value="testo">Testo</option>
                                    <option value="testo_lungo">Testo lungo</option>
                                    <option value="numero">Numero</option>
                                    <option value="data">Data</option>
                                    <option value="scelta_singola">Scelta fra più voci</option>
                                </select>
                            </label>
                            {richiesta.tipo === 'numero' && (
                                <label>
                                    Unità di misura
                                    <input value={richiesta.unita}
                                           onChange={(e) => setRichiesta({ ...richiesta, unita: e.target.value })}
                                           placeholder="kg, cm, mmHg…" />
                                </label>
                            )}
                        </div>
                        <label>
                            Perché ti serve
                            <textarea rows={3} required value={richiesta.motivo}
                                      onChange={(e) => setRichiesta({ ...richiesta, motivo: e.target.value })}
                                      placeholder="Almeno dieci caratteri." />
                            <small>È quello che permette a chi decide di risponderti invece di tirare a indovinare.</small>
                        </label>
                        <div className="cat-pulsanti">
                            <button type="button" className="btn-annulla" onClick={() => setRichiesta(null)}>Annulla</button>
                            <button type="submit" className="btn-primario">Invia la richiesta</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default MieSchede;
